import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AlertCircle, Ban, BellRing, CalendarClock, Check, CheckCheck, ChevronDown, Clock3, Eye, MoreHorizontal, Pencil, Search, Send, Trash2, UserX } from "lucide-react";
import { Avatar } from "../crm/ui";
import { fmtPhone, initialsFor } from "../crm/contactsKit";
import { Drawer } from "../Drawer";
import { DAY, NO_DATA } from "../../lib/format";
import { APPOINTMENT_STATUSES, APPOINTMENT_TYPES, PRIORITIES, VITALS, labelOf } from "./appointmentsData";

// Design: components/appointments/AppointmentRow.tsx. Rows carry the patient's
// name and phone from the appointment itself; the patient links to /patients/:id.

/* ---------- Status look, shared by row, picker and view ---------- */

const TONE = {
  Requested: "bg-sun-soft text-ink",
  Booked: "bg-mist text-ink",
  Confirmed: "bg-paper text-ink ring-1 ring-ink/25",
  "Checked in": "bg-sky-soft text-ink",
  "In consultation": "bg-sky text-ink",
  Completed: "bg-success-soft text-success",
  "No-show": "bg-warning-soft text-warning",
  Cancelled: "bg-mist text-muted",
};
const DOT = {
  Requested: "bg-sun",
  Booked: "bg-muted/60",
  Confirmed: "bg-ink",
  "Checked in": "bg-sky-deep",
  "In consultation": "bg-ink",
  Completed: "bg-success",
  "No-show": "bg-warning",
  Cancelled: "bg-muted",
};
/** Visit flow first, then the two ways a visit doesn't happen. */
const GROUPS = [
  ["Requested", "Booked", "Confirmed", "Checked in", "In consultation", "Completed"],
  ["No-show", "Cancelled"],
];

export function AppointmentStatusBadge({ status }) {
  return (
    <span className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-caption font-semibold ${TONE[status] || TONE.Booked}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[status] || DOT.Booked}`} />
      {status}
    </span>
  );
}

export const timeLabel = (ts) =>
  new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(ts).replace(/\b(am|pm)\b/i, (p) => p.toUpperCase());
const dayLabel = (ts) => new Intl.DateTimeFormat("en-IN", { weekday: "short", day: "numeric", month: "short" }).format(ts);
const startOfDay = (ts) => {
  const d = new Date(ts);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/* ---------- Floating menu (portal, flips above when there is no room) ---------- */

function useFloating(open, setOpen, height, width) {
  const anchor = useRef(null);
  const menu = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const r = anchor.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom > height + 12;
    setPos({
      left: Math.floor(Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8))),
      top: Math.floor(below ? r.bottom + 6 : Math.max(8, r.top - height - 6)),
    });
  }, [open, height, width]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "mousedown" && (menu.current?.contains(e.target) || anchor.current?.contains(e.target))) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, setOpen]);
  return { anchor, menu, pos };
}

/** Status badge that opens a picker. Same pattern as the lead status picker. */
export function AppointmentStatusPicker({ status, onChange, label }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { anchor, menu, pos } = useFloating(open, setOpen, 340, 216);
  const visibleGroups = GROUPS.map((group) => group.filter((item) => item.toLowerCase().includes(query.trim().toLowerCase()))).filter((group) => group.length > 0);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status of ${label}: ${status}. Change status`}
        onClick={() => { setQuery(""); setOpen((o) => !o); }}
        className="group/status -ml-1 flex w-fit items-center gap-0.5 rounded-full p-0.5 outline-none hover:ring-1 hover:ring-line focus-visible:ring-2 focus-visible:ring-ink/20"
      >
        <AppointmentStatusBadge status={status} />
        <ChevronDown size={14} strokeWidth={1.75} className="mr-1 text-muted opacity-0 transition-opacity group-hover/status:opacity-100 group-aria-expanded/status:opacity-100" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div ref={menu} role="listbox" aria-label="Appointment status" style={{ top: pos.top, left: pos.left }} className="olli fixed z-[70] w-[216px] animate-nav-in rounded-md bg-paper p-1.5 font-sans text-ink shadow-md ring-1 ring-line">
            <label className="flex h-9 items-center gap-2 border-b border-line px-2.5">
              <Search size={14} className="shrink-0 text-muted" />
              <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search statuses" aria-label="Search appointment statuses" className="min-w-0 flex-1 bg-transparent text-caption outline-none placeholder:text-muted" />
            </label>
            {visibleGroups.map((g, gi) => (
              <div key={gi} className={gi ? "mt-1.5 border-t border-line pt-1.5" : ""}>
                {g.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="option"
                    aria-selected={s === status}
                    onClick={() => {
                      setOpen(false);
                      if (s !== status) onChange(s);
                    }}
                    className={`flex h-9 w-full items-center gap-2.5 rounded-xs px-3 text-left text-small ${s === status ? "bg-mist font-semibold" : "hover:bg-mist"}`}
                  >
                    <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[s]}`} />
                    <span className="flex-1">{s}</span>
                    {s === status && <Check size={14} strokeWidth={2} />}
                  </button>
                ))}
              </div>
            ))}
            {visibleGroups.length === 0 && <p className="px-3 py-3 text-small text-muted">No statuses match.</p>}
          </div>,
          document.body,
        )}
    </>
  );
}

/* ---------- Row ---------- */

/** "Chair 2", or "No chair" (item 174). */
export const chairLabel = (a) => (a.chairId ? `Chair ${a.chairId}` : "No chair");

/** The reminder line: icon and words for the last reminder's WhatsApp state (item 173). */
const REMINDER_TEXT = { "Not sent": "Reminder not sent", Sent: "Reminder sent", Delivered: "Reminder delivered", Read: "Reminder read", Failed: "Reminder failed" };
export function ReminderState({ a, size = 12 }) {
  const icon = a.reminder === "Read" ? <CheckCheck size={size} strokeWidth={2} className="text-success" />
    : a.reminder === "Delivered" ? <CheckCheck size={size} strokeWidth={1.75} />
      : a.reminder === "Failed" ? <AlertCircle size={size} strokeWidth={1.75} className="text-danger" />
        : a.reminder === "Not sent" ? <Clock3 size={size} strokeWidth={1.75} /> : <Send size={size} strokeWidth={1.75} />;
  const when = a.reminderAt ? ` · ${new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(a.reminderAt)}` : "";
  return <span className={`inline-flex items-center gap-1 ${a.reminder === "Failed" ? "text-danger" : ""}`} title={a.reminder !== "Not sent" ? `${REMINDER_TEXT[a.reminder] || a.reminder}${when}` : undefined}>{icon}{REMINDER_TEXT[a.reminder] || a.reminder}</span>;
}

/** The next step for this visit, shown as the row's one text action. */
export function nextStep(a) {
  const now = Date.now();
  const today = startOfDay(now);
  const isToday = a.start >= today && a.start < today + DAY;
  // A request (Olli's agent, an on-call slot) is confirmed by the clinic first.
  if (a.status === "Requested") return { label: "Confirm", run: (h) => h.onStatus(a, "Confirmed") };
  if (isToday && (a.status === "Booked" || a.status === "Confirmed")) return { label: "Check in", run: (h) => h.onStatus(a, "Checked in") };
  // Old app: Check in → Start consultation (in_progress; the backend records the start time) → Complete.
  if (a.status === "Checked in") return { label: "Start consultation", run: (h) => h.onStatus(a, "In consultation") };
  if (a.status === "In consultation") return { label: "Complete", run: (h) => h.onStatus(a, "Completed") };
  if (a.start > now && (a.reminder === "Not sent" || a.reminder === "Failed") && (a.status === "Booked" || a.status === "Confirmed")) return { label: a.reminder === "Failed" ? "Send again" : "Send reminder", run: (h) => h.onReminder(a), reminder: true };
  return null;
}

export const ROW_GRID = "grid grid-cols-[88px_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.1fr)_184px_288px] items-center gap-4";

export function AppointmentListHeader() {
  return (
    <div role="row" className={`${ROW_GRID} min-w-[1040px] border-b border-line px-5 py-2.5 text-caption font-medium text-muted`}>
      <span>Time</span>
      <span>Patient</span>
      <span>Service</span>
      <span>Doctor and chair</span>
      <span>Status</span>
      <span className="text-right">Actions</span>
    </div>
  );
}

const patientName = (a) => a.patient || "Unknown patient";
const clock12 = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

export function AppointmentRow({ appointment: a, index, handlers }) {
  const [more, setMore] = useState(false);
  const { anchor, menu, pos } = useFloating(more, setMore, 200, 220);
  const name = patientName(a);
  const step = nextStep(a);
  const past = a.start + a.duration * 60_000 < Date.now();
  const iconBtn =
    "flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors duration-150 hover:bg-paper hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20";
  const fromControl = (e) => !!e.target.closest("button, a, [data-row-control]");
  const item = "flex h-9 w-full items-center gap-2.5 rounded-xs px-3 text-left text-small hover:bg-mist";

  return (
    <div
      role="row"
      tabIndex={0}
      onClick={(e) => !fromControl(e) && handlers.onView(a)}
      onKeyDown={(e) => e.key === "Enter" && !fromControl(e) && handlers.onView(a)}
      style={{ animationDelay: `${Math.min(index, 12) * 22}ms` }}
      className={`${ROW_GRID} group/row min-w-[1040px] animate-fade-in cursor-pointer px-5 py-3 outline-none hover:bg-mist/60 focus-visible:bg-mist`}
    >
      <div>
        <p className={`text-small font-semibold tabular-nums ${a.status === "Cancelled" ? "text-muted line-through" : ""}`}>{timeLabel(a.start)}</p>
        <p className="text-caption text-muted">{a.duration} min</p>
      </div>
      <div className="flex min-w-0 items-center gap-3">
        <Avatar name={name} initials={a.patient ? initialsFor(a.patient) : "?"} size="sm" />
        <div className="min-w-0">
          <p className="truncate text-small font-semibold">{name}</p>
          <p className="text-caption text-muted">{a.patientPhone ? fmtPhone(a.patientPhone) : a.code}</p>
        </div>
      </div>
      <div className="min-w-0">
        <p className="truncate text-small">{a.service}</p>
        {(a.type !== "consultation" || a.priority !== "normal") && <p className="truncate text-caption text-muted">{[a.type !== "consultation" && labelOf(APPOINTMENT_TYPES, a.type), a.priority !== "normal" && <span key="p" className="font-semibold text-danger">{labelOf(PRIORITIES, a.priority)}</span>].filter(Boolean).reduce((out, x, i) => (i ? [...out, " · ", x] : [x]), [])}</p>}
      </div>
      <div className="min-w-0">
        <p className="truncate text-small">{a.doctor}</p>
        <p className="truncate text-caption text-muted">
          {chairLabel(a)} · {a.facility || NO_DATA}
        </p>
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <AppointmentStatusPicker status={a.status} label={name} onChange={(s) => handlers.onStatus(a, s)} />
        <span className="flex items-center gap-1 text-caption text-muted">
          {handlers.isSending?.(a) ? <><Send size={12} strokeWidth={1.75} />Sending reminder…</> : <ReminderState a={a} />}
        </span>
      </div>
      <div className="flex items-center justify-end gap-0.5" data-row-control>
        {step && (
          <button type="button" disabled={step.reminder && handlers.isSending?.(a)} onClick={() => step.run(handlers)} className="mr-1 h-9 whitespace-nowrap rounded-full px-3 text-caption font-semibold text-sky-deep ring-1 ring-line hover:bg-sky-soft hover:ring-sky-soft disabled:opacity-50">
            {step.reminder && handlers.isSending?.(a) ? "Sending…" : step.label}
          </button>
        )}
        <button type="button" onClick={() => handlers.onView(a)} aria-label={`View appointment for ${name}`} title="View" className={iconBtn}>
          <Eye size={16} strokeWidth={1.75} />
        </button>
        <button type="button" onClick={() => handlers.onEdit(a)} aria-label={`Edit appointment for ${name}`} title="Edit" className={iconBtn}>
          <Pencil size={16} strokeWidth={1.75} />
        </button>
        <button type="button" onClick={() => handlers.onDelete(a)} aria-label={`Delete appointment for ${name}`} title="Delete" className={`${iconBtn} hover:text-danger`}>
          <Trash2 size={16} strokeWidth={1.75} />
        </button>
        <button ref={anchor} type="button" onClick={() => setMore((v) => !v)} aria-haspopup="menu" aria-expanded={more} aria-label={`More actions for ${name}`} title="More" className={iconBtn}>
          <MoreHorizontal size={17} strokeWidth={1.75} />
        </button>
        {more &&
          pos &&
          createPortal(
            <div ref={menu} role="menu" style={{ top: pos.top, left: pos.left }} className="olli fixed z-[70] w-[220px] animate-nav-in rounded-md bg-paper p-1.5 font-sans text-ink shadow-md ring-1 ring-line">
              <button role="menuitem" type="button" className={item} onClick={() => { setMore(false); handlers.onEdit(a); }}>
                <CalendarClock size={15} strokeWidth={1.75} className="text-muted" />
                Reschedule
              </button>
              {/* The backend sends reminders for booked and confirmed visits only (item 172). */}
              {!past && ["Requested", "Booked", "Confirmed"].includes(a.status) && (
                <button role="menuitem" type="button" disabled={handlers.isSending?.(a)} className={item} onClick={() => { setMore(false); handlers.onReminder(a); }}>
                  <BellRing size={15} strokeWidth={1.75} className="text-muted" />
                  {a.reminder === "Not sent" ? "Send WhatsApp reminder" : "Send reminder again"}
                </button>
              )}
              {a.status !== "No-show" && a.status !== "Cancelled" && a.status !== "Completed" && (
                <button role="menuitem" type="button" className={item} onClick={() => { setMore(false); handlers.onStatus(a, "No-show"); }}>
                  <UserX size={15} strokeWidth={1.75} className="text-muted" />
                  Mark no-show
                </button>
              )}
              {a.status !== "Cancelled" && (
                <div className="mt-1.5 border-t border-line pt-1.5">
                  <button role="menuitem" type="button" className={`${item} text-danger hover:bg-danger-soft`} onClick={() => { setMore(false); handlers.onStatus(a, "Cancelled"); }}>
                    <Ban size={15} strokeWidth={1.75} />
                    Cancel appointment
                  </button>
                </div>
              )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

/* ---------- View drawer (read-only, with status and next step) ---------- */

/**
 * `history`: the patient's other appointments, newest first; null while loading.
 */
export function AppointmentViewDrawer({ appointment: a, history, open, onClose, handlers }) {
  if (!a) return null;
  const name = patientName(a);
  const end = a.start + a.duration * 60_000;
  const step = nextStep(a);
  const created = a.createdAt ? new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short" }).format(a.createdAt) : NO_DATA;
  const firstName = a.patient ? a.patient.split(/\s+/)[0] : "";
  const row = (label, value) => (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="text-caption text-muted">{label}</dt>
      <dd className="text-right text-small">{value}</dd>
    </div>
  );
  return (
    <Drawer
      open={open}
      onClose={onClose}
      labelledBy="appt-view-title"
      closeLabel="Close"
      context={`${a.code} · booked by ${a.source} on ${created}`}
      title={name}
      danger={
        <button type="button" onClick={() => handlers.onDelete(a)} className="flex h-10 items-center gap-1.5 rounded-full px-3 text-small font-semibold text-danger hover:bg-danger-soft">
          <Trash2 size={15} strokeWidth={1.75} />
          Delete
        </button>
      }
      actions={
        <button type="button" onClick={() => handlers.onEdit(a)} className="flex h-11 items-center gap-2 rounded-full bg-sun px-5 text-small font-semibold text-ink hover:brightness-95">
          <Pencil size={15} strokeWidth={1.75} />
          Edit appointment
        </button>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="rounded-lg bg-mist p-4">
          <p className={`text-h3 font-semibold tracking-tight ${a.status === "Cancelled" ? "text-muted line-through" : ""}`}>
            {dayLabel(a.start)}, {timeLabel(a.start)} to {timeLabel(end)}
          </p>
          <p className="mt-1 text-small text-muted">
            {a.service} with {a.doctor}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <AppointmentStatusPicker status={a.status} label={name} onChange={(s) => handlers.onStatus(a, s)} />
            {step && (
              <button type="button" disabled={step.reminder && handlers.isSending?.(a)} onClick={() => step.run(handlers)} className="h-9 rounded-full bg-paper px-3.5 text-caption font-semibold text-sky-deep ring-1 ring-line hover:bg-sky-soft disabled:opacity-50">
                {step.reminder && handlers.isSending?.(a) ? "Sending…" : step.label}
              </button>
            )}
            {a.status !== "Cancelled" && a.status !== "Completed" && (
              <button type="button" onClick={() => handlers.onStatus(a, "Cancelled")} className="h-9 rounded-full px-3.5 text-caption font-semibold text-danger hover:bg-danger-soft">
                Cancel appointment
              </button>
            )}
          </div>
        </div>

        <dl className="divide-y divide-line">
          {row("Patient", a.patientId ? <Link to={`/patients/${a.patientId}`} className="font-medium text-sky-deep hover:underline">{name}</Link> : name)}
          {a.patientPhone && row("Phone", fmtPhone(a.patientPhone))}
          {row("Chair", `${chairLabel(a)} · ${a.facility || NO_DATA}`)}
          {a.leadId && row("Lead", <Link to={`/crm/leads/${a.leadId}`} className="font-medium text-sky-deep hover:underline">Lead #{a.leadId}</Link>)}
          {row("Duration", `${a.duration} min`)}
          {row("Type", labelOf(APPOINTMENT_TYPES, a.type) || NO_DATA)}
          {a.priority !== "normal" && row("Priority", <span className="font-semibold text-danger">{labelOf(PRIORITIES, a.priority)}</span>)}
          {row("Booked via", a.source)}
          {/* The old app's "Is first visit": no earlier appointment for this patient. */}
          {history !== null && row("First visit", history.some((h) => h.start < a.start) ? "No" : "Yes")}
          {a.checkedInAt && row("Checked in", new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(a.checkedInAt))}
          {a.consultStart && row("Consultation", `${clock12(a.consultStart)}${a.consultEnd ? ` to ${clock12(a.consultEnd)}` : " · in progress"}`)}
          {row(
            "WhatsApp reminder",
            <span className="inline-flex items-center gap-1.5">
              <ReminderState a={a} size={13} />
              {a.start > Date.now() && ["Requested", "Booked", "Confirmed"].includes(a.status) && (
                <button type="button" disabled={handlers.isSending?.(a)} onClick={() => handlers.onReminder(a)} className="ml-1 text-caption font-semibold text-sky-deep hover:underline disabled:opacity-50">
                  {handlers.isSending?.(a) ? "Sending…" : a.reminder === "Not sent" ? "Send now" : "Send again"}
                </button>
              )}
            </span>,
          )}
          {/* Booking confirmation (POST …/send-confirmation); the backend sends it for requested, booked and confirmed visits. */}
          {handlers.onConfirmation && a.start > Date.now() && ["Requested", "Booked", "Confirmed"].includes(a.status) && (() => {
            const c = handlers.confirmation?.(a) || {};
            return (
              <div className="py-2.5">
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-caption text-muted">WhatsApp confirmation</dt>
                  <dd className="inline-flex items-center gap-1.5 text-right text-small">
                    {c.sentAt && !c.sending && <span className="inline-flex items-center gap-1 text-caption text-muted"><Check size={13} strokeWidth={2} />Sent {timeLabel(new Date(c.sentAt).getTime())}</span>}
                    <button type="button" disabled={!!c.sending} onClick={() => handlers.onConfirmation(a)} className="ml-1 text-caption font-semibold text-sky-deep hover:underline disabled:opacity-50">
                      {c.sending ? "Sending…" : c.sentAt || c.error ? "Send again" : "Send confirmation"}
                    </button>
                  </dd>
                </div>
                {c.error && !c.sending && <p role="alert" className="mt-1.5 text-caption text-danger">{c.error}</p>}
              </div>
            );
          })()}
        </dl>

        {a.status === "Cancelled" && a.cancellationReason && (
          <div className="rounded-md bg-danger-soft px-4 py-3">
            <h3 className="text-caption font-semibold text-danger">Cancellation reason</h3>
            <p className="mt-1 whitespace-pre-line text-small text-danger">{a.cancellationReason}</p>
          </div>
        )}

        {a.chiefComplaint && (
          <div>
            <h3 className="text-small font-semibold">Chief complaint</h3>
            <p className="mt-1.5 whitespace-pre-line text-small text-muted">{a.chiefComplaint}</p>
          </div>
        )}

        {Object.keys(a.vitals || {}).length > 0 && (
          <div>
            <h3 className="text-small font-semibold">Vitals</h3>
            <dl className="mt-2 grid grid-cols-3 gap-2">
              {VITALS.filter(([key]) => a.vitals[key]).map(([key, label, unit]) => (
                <div key={key} className="rounded-sm bg-mist px-3 py-2">
                  <dt className="text-caption text-muted">{label}</dt>
                  <dd className="text-small font-semibold tabular-nums">{a.vitals[key]} {unit}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {a.notes && (
          <div>
            <h3 className="text-small font-semibold">Notes</h3>
            <p className="mt-1.5 whitespace-pre-line text-small text-muted">{a.notes}</p>
          </div>
        )}

        {a.internalNotes && (
          <div className="rounded-md bg-sun-soft px-4 py-3">
            <h3 className="text-caption font-semibold">Internal notes</h3>
            <p className="mt-1 whitespace-pre-line text-small">{a.internalNotes}</p>
          </div>
        )}

        <div>
          <h3 className="text-small font-semibold">Other visits</h3>
          {history === null ? (
            <p className="mt-1.5 text-small text-muted">{NO_DATA}</p>
          ) : history.length === 0 ? (
            <p className="mt-1.5 text-small text-muted">First appointment for {firstName || "this patient"}.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {history.slice(0, 4).map((h) => (
                <li key={h.id} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="min-w-0">
                    <span className="block truncate text-small">{h.service}</span>
                    <span className="text-caption text-muted">
                      {dayLabel(h.start)} · {h.doctor}
                    </span>
                  </span>
                  <AppointmentStatusBadge status={h.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Drawer>
  );
}

export { APPOINTMENT_STATUSES };
